import {ICompletedAttemptResult, TutorialAttemptApi, TutorialAttemptDto} from "@/Api/TutorialAttemptApi";
import {
    CategoryAnswer,
    CompletedQuestion, IAskCategoryQuestion, IAskMultipleChoiceQuestion,
    IAskQuestion, ICategoryQuestionItem,
    ICompletedCategoryQuestion,
    ICompletedMultipleChoiceQuestion, ICompletedOrderQuestion, IMultipleChoiceOption, IOrderOption
} from "@/Core/Interactions";
import {
    IQuestionAnswerContext,
    IQuestionContext,
    markCategoryQuestion,
    markChoiceQuestion,
    markSequenceQuestion
} from "@/Core/Marking";
import {
    convertToAskQuestion,
    ICategory,
    ICategoryQuestion,
    ICategoryQuestionAnswer, IMultipleChoiceQuestion, IOrderQuestion,
    IQuestion,
    Question
} from "@/CreatorTools/Model/Questions";
import {inject, injectable} from "inversify";
import {StateValue, TutorialState} from "@/Core/TutorialState";
import {ISlideshowBuilder, SlideshowInformation} from "@/Core/SlideshowBuilder";
import {buildSlideGraph, PlayableAudio, SlideData} from "@/Core/SlideshowData";
import {toLocalString} from "@/i18n/MultilingualText";
import {DirectedAcyclicGraph} from "@/Core/Dag";

/**
 * This implementation is designed for working with the content creation
 * suite and provides a means to reset progress.
 * **/
@injectable()
export class StandaloneAttemptApi implements TutorialAttemptApi, ISlideshowBuilder {
    private _data: TutorialData;
    private _questions: Question[] = [];
    private _completedQuestions: CompletedQuestion[] = [];

    constructor(
        @inject("TutorialState") private _state: TutorialState) {

    }

    private _fetchPromise: Promise<void>|null = null;

    private async fetchData(): Promise<void> {
        if (this._fetchPromise == null) {
            let responsePromise = fetch("standalone.json");
            this._fetchPromise = responsePromise
                .then(x => x.json())
                .then(x => {
                    this._data = x;
                    this._questions = StandaloneAttemptApi.convertQuestionContexts(this._data.questions);
                });
        }

        await this._fetchPromise;
    }

    private _slideshowInformation: SlideshowInformation|null = null;
    private _slides = new Map<string, SlideData>();

    async build(): Promise<SlideshowInformation> {
        await this.fetchData();
        for (let slide of this._data.slides)
            this._slides.set(slide.id, slide);
        this._slideshowInformation = {
            questions: this._questions.map(x => convertToAskQuestion(x)),
            title: this._data.metadata.title,
            css: this._data.metadata.css,
            audio: this._data.metadata.playableAudio,
            supplierText: null,
            supportedLanguageCodes: [this._data.metadata.language],
            slides: this._data.slides,
            graph: buildSlideGraph(this._data.slides)
        };
        return this._slideshowInformation;
    }

    getContent(id: string): Promise<SlideData> {
        let slide = this._slides.get(id);
        if (slide == null)
            throw "Slide not found";
        return Promise.resolve(slide);
    }

    get tutorialAttempt() : TutorialAttemptDto {
        return {
            completed: false,
            questions: this._questions.map(x => convertToAskQuestion(x)),
            variables: {
                customer: {
                    customerId: 0,
                    supportEmail: "support@hutsix.io",
                    customerName: "Example Customer",
                    supplierLogoGreySrc: null,
                    supplierLogoColorSrc: null,
                    appearance: {
                        tutorialLineColour: "#023149",
                        primaryColour: "#023149",
                        logoForContent: null,
                        logoForBranding: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                    }
                },
                course: {},
                user: {
                    name: "Example User",
                    email: "user@example.com"
                }
            },
            isApproval: false,
        };
    }

    async startOrContinueAttempt(): Promise<TutorialAttemptDto> {
        this._state.setValue(["appearance"], {
            "primary_color": "#023149",
            "accent_color": "#023149"
        });

        let defaultCustomerLogo = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23cccccc'%3E%3C/rect%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='monospace' font-size='26px' fill='%23333333'%3ECustomer Logo%3C/text%3E%3C/svg%3E`;
        this._state.setValue(["customer"], {
            name: "Example Customer",
            "support_email": "support@example.com",
            "logo_url": defaultCustomerLogo,
            "grayscale_logo_url": defaultCustomerLogo,
        });
        this._state.setValue(["user"], {
            email: "john.doe@example.com",
            first_name: "John",
            last_name: "Doe",
            display_name: "John Doe",
        });
        return Promise.resolve(this.tutorialAttempt);
    }

    endTutorialAttempt(): Promise<ICompletedAttemptResult> {
        let completionData = {
            completed: true,
            passInformation: null
        };
        localStorage.setItem('hutsix.tutorials.' + this._data.metadata.title, JSON.stringify(completionData));
        return Promise.resolve(completionData);
    }

    canExit(): boolean {
        return true;
    }

    exit(): void {
        window.history.back();
    }

    getCompletedQuestion(questionCode: string): CompletedQuestion | null {
        return this._completedQuestions.find(q => q.questionCode === questionCode) ?? null;
    }

    getCompletedQuestions(): CompletedQuestion[] {
        return this._completedQuestions;
    }

    getQuestion(questionCode: string): IAskQuestion | null {
        let q = this._questions.find(x => x.id === questionCode);
        return q != null ? convertToAskQuestion(q) : null;
    }

    submitCategoriseQuestion(questionCode: string, answers: CategoryAnswer[]): Promise<ICompletedCategoryQuestion> {
        let question = this._questions.find(q => q.id === questionCode);
        if (question == null || question.questionType != "category")
            throw "Could not find category question with code " + questionCode;

        let completedQuestion = markCategoryQuestion(question, answers);
        this._completedQuestions.push(completedQuestion);
        this.updateTutorialState(completedQuestion);
        return Promise.resolve(completedQuestion);
    }

    submitMultipleChoice(questionCode: string, answers: string[]): Promise<ICompletedMultipleChoiceQuestion> {
        let question = this._questions.find(q => q.id === questionCode);
        if (question == null || question.questionType != "choice")
            throw "Could not find choice question with code " + questionCode;

        let completedQuestion = markChoiceQuestion(question, answers);
        this._completedQuestions.push(completedQuestion);
        this.updateTutorialState(completedQuestion);
        return Promise.resolve(completedQuestion);
    }

    submitOrderQuestion(questionCode: string, answerCodes: string[]): Promise<ICompletedOrderQuestion> {
        let question = this._questions.find(q => q.id === questionCode);
        if (question == null || question.questionType != "sequence")
            throw "Could not find sequence question with code " + questionCode;

        let completedQuestion = markSequenceQuestion(question, answerCodes);
        this._completedQuestions.push(completedQuestion);
        this.updateTutorialState(completedQuestion);
        return Promise.resolve(completedQuestion);
    }

    private updateTutorialState(cq: CompletedQuestion) {
        let obj: any = {
            is_correct: cq.isCorrect,
            mark: cq.totalMark,
            feedback: cq.feedback,
        };
        if (cq.questionType === "choice") {
            obj.given_answers = cq.givenAnswers.map(x => x.questionAnswerCode);
        }

        this._state.setValue(['questions', cq.questionCode, 'attempted'], true);
        this._state.setValue(['questions', cq.questionCode, 'attempts', 'latest'], obj);
        this._state.setValue(['questions', cq.questionCode, 'attempts', 0], obj);
    }

    submitTutorialApproval(isApproved: boolean, approvalNotes: string): Promise<void> {
        return Promise.resolve(undefined);
    }

    private static convertQuestionContexts(questionContexts: IQuestionContext[]): Question[] {
        let list: Question[] = [];

        for (let q of questionContexts) {
            let common : IQuestion = {
                questionType: q.questionType,
                id: q.code,
                text: q.questionText,
                minAnswers: q.minAnswers,
                maxAnswers: q.maxAnswers,
                maxAttempts: q.maxAnswers,
                feedback: q.feedback
            };

            if (common.questionType === "choice") {
                let choiceProps: Omit<IMultipleChoiceQuestion, keyof IQuestion> = {
                    answers: q.answers.map(a => ({
                        id: a.code,
                        text: a.answerText,
                        feedback: a.answerFeedback,
                        isCorrect: <boolean> a.isCorrect
                    }))
                };
                list.push(<IMultipleChoiceQuestion> {...common, ...choiceProps});
            }
            else if (common.questionType === "sequence") {
                let correctOrder = q.answers
                    .filter(a => a.correctPosition != null)
                    .sort((a, b) => {
                        let positionA = <number> a.correctPosition;
                        let positionB = <number> b.correctPosition;
                        if (positionA === positionB)
                            return 0;
                        return positionA < positionB ? -1 : 1
                    })
                    .map(a => a.code);
                let orderProps: Omit<IOrderQuestion, keyof IQuestion> = {
                    items: q.answers.map(a => ({
                        id: a.code,
                        text: a.answerText,
                        feedback: a.answerFeedback,
                    })),
                    patterns: [
                        {
                            codes: correctOrder
                        }
                    ]
                };
                list.push(<IOrderQuestion> {...common, ...orderProps});
            }
            else if (common.questionType === "category") {
                let catQuestionProps: Omit<ICategoryQuestion, keyof IQuestion> = {
                    categories: <ICategory[]> [],
                    items: q.answers.map(a => ({
                        id: a.code,
                        text: a.answerText,
                        feedback: a.answerFeedback
                    }))
                };

                for (let category in q.categories) {
                    catQuestionProps.categories.push({
                        id: category,
                        items: q.answers.filter(a => a.correctCategory === category).map(a => a.code),
                        text: q.categories[category]
                    });
                }

                list.push(<ICategoryQuestion> {...common, ...catQuestionProps});
            }
            else {
                throw "Unknown question type";
            }
        }

        return list;
    }

    private static convertToChoiceOption(context: IQuestionAnswerContext): IMultipleChoiceOption {
        return {
            itemText: toLocalString(context.answerText),
            code: context.code
        };
    }

    private static convertToOrderAnswer(context: IQuestionAnswerContext): IOrderOption {
        return {
            itemText: toLocalString(context.answerText),
            code: context.code
        };
    }

    private static convertToCategoryAnswer(context: IQuestionAnswerContext): ICategoryQuestionItem {
        return {
            itemText: toLocalString(context.answerText),
            code: context.code
        };
    }
}

interface TutorialData {
    metadata: {
        language: string;
        title: string;
        states: {variableName: string, initialValue: StateValue}[],
        playableAudio: PlayableAudio[],
        css: string,
//         public string Language { get; set; }
// public string Title { get; set; }
// public List<StandaloneStateDefinition> States { get; set; }
// public List<AudioInformation> PlayableAudio { get; set; }
// public string Css { get; set; }
    };
    slides: SlideData[];
    questions: IQuestionContext[];
}